import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from 'react/components/common/Tooltip/Tooltip';
import Icon from 'react/components/common/Icon/Icon';
import unselectedUpArrow from 'icons/unselected_up_arrow.svg';
import unselectedDownArrow from 'icons/unselected_down_arrow.svg';
import selectedUpArrow from 'icons/selected_up_arrow.svg';
import selectedDownArrow from 'icons/selected_down_arrow.svg';
import { useCategoriesData } from './hooks';
import { CategoryItemRow } from './components';
import './CategoriesTable.scss';

const SortColumnArrows = ({ columnId, sortConfig }) => {
  const active = sortConfig.order === columnId;
  const desc = sortConfig.reverse;
  const upSrc = !active || desc ? unselectedUpArrow : selectedUpArrow;
  const downSrc = !active || !desc ? unselectedDownArrow : selectedDownArrow;
  return (
    <span className="column-sort-arrows" aria-hidden="true">
      <img className="sort-arrow sort-arrow--up" src={upSrc} alt="" />
      <img className="sort-arrow sort-arrow--down" src={downSrc} alt="" />
    </span>
  );
};

SortColumnArrows.propTypes = {
  columnId: PropTypes.string.isRequired,
  sortConfig: PropTypes.shape({
    order: PropTypes.string,
    reverse: PropTypes.bool,
  }).isRequired,
};

const DEFAULT_COLUMNS = [
  { id: 'phrase', title: 'Categories', flex: '3', tooltip: '' },
  {
    id: 'uniqueness-index',
    title: 'Audience Skew',
    flex: '0-4',
    tooltip: 'The extent to which the category is over-indexed among the audience.',
  },
  {
    id: 'interest-portion',
    title: 'Category Portion In Audience',
    flex: '0-7',
    tooltip:
      'How popular the category is within the audience. 100% means everyone in the audience are included in the category',
  },
  {
    id: 'segment-portion',
    title: 'Audience Portion In Category',
    flex: '0-7',
    tooltip: 'How unique the category is to the audience. 100% means only this audience builds up the category',
  },
  { id: 'reach', title: 'Category Size', flex: '0-4', tooltip: 'Total number of users in the category' },
  { id: 'id', title: 'DMP Category ID', flex: '0-4', tooltip: '' },
];

const CategoriesTable = ({
  data,
  columns,
  hiddenColumnIds,
  loading,
  title,
  selectedCategory,
  onSelectCategory,
  isCategoryTargetingVisible,
}) => {
  const searchInputRef = useRef(null);
  const {
    search,
    searchOpened,
    handleToggleSearch,
    handleSearchChange,
    clearSearch,
    groupedData,
    sortedData,
    groupByPrimaryCategory,
    setGroupByPrimaryCategory,
    totalCategories,
    sortConfig,
    handleSort,
  } = useCategoriesData({ data, searchInputRef });

  const categoriesColumns = (columns || DEFAULT_COLUMNS).filter((column) => !hiddenColumnIds.includes(column.id));

  return (
    <div className="categories-table-wrapper">
      <div className="categories-table">
        <div className="categories-table-toolbar">
          <span className="toolbar-title">{title || 'All Categories'}</span>
          <label className="group-toggle">
            <div
              className="group-toggle-checkbox"
              role="checkbox"
              aria-checked={groupByPrimaryCategory}
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                setGroupByPrimaryCategory((prev) => !prev);
              }}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setGroupByPrimaryCategory((prev) => !prev);
                }
              }}
            >
              <Icon
                iconId={groupByPrimaryCategory ? 'selected-checkbox' : 'unselected-checkbox'}
                className={classNames('category-checkbox-icon', { 'selected-checkbox-icon': groupByPrimaryCategory })}
              />
            </div>
            <span>Group by Primary Category</span>
          </label>
        </div>
        {/* Header */}
        <div className="categories-table-header">
          {categoriesColumns.map((column, index) => (
            <Tooltip
              key={column.id}
              content={column.tooltip}
              options={{ popperOptions: { modifiers: { flip: { enabled: false } } } }}
            >
              <div
                className={classNames('column', `col-${column.id}`, `flex${column.flex || '1'}`, {
                  sortable: true,
                })}
                onClick={() => handleSort(column.id)}
              >
                {/* Search in first column */}
                {index === 0 && (
                  <span
                    className={classNames('search', { selected: search, opened: searchOpened })}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="icon-Search pointer" onClick={handleToggleSearch}></i>
                    {searchOpened && (
                      <span className="search-input-wrapper">
                        <input
                          ref={searchInputRef}
                          value={search}
                          onChange={handleSearchChange}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <i className="icon-X_CLEAR pointer" onClick={clearSearch}></i>
                      </span>
                    )}
                  </span>
                )}
                {(index !== 0 || !searchOpened) && (
                  <>
                    <span className="column-title">{column.title}</span>
                    <SortColumnArrows columnId={column.id} sortConfig={sortConfig} />
                  </>
                )}
                {index === 0 && <span className="count">({totalCategories})</span>}
              </div>
            </Tooltip>
          ))}
          <div className="column scroll-td"></div>
        </div>

        {/* Body */}
        <div
          className={classNames('categories-table-body', {
            loading,
            grouped: groupByPrimaryCategory,
            flat: !groupByPrimaryCategory,
          })}
        >
          {groupByPrimaryCategory
            ? groupedData.map((group, groupIndex) => (
                <div
                  key={group.id}
                  className={classNames('grouped-flat-group', {
                    'group-odd': groupIndex % 2 === 0,
                    'group-even': groupIndex % 2 === 1,
                  })}
                >
                  {group.children.map((category) => (
                    <div key={category.id} className="grouped-flat-row">
                      <CategoryItemRow
                        category={category}
                        columns={categoriesColumns}
                        isSelected={selectedCategory?.id === category.id}
                        onSelect={onSelectCategory}
                        isCategoryTargetingVisible={isCategoryTargetingVisible}
                      />
                    </div>
                  ))}
                </div>
              ))
            : sortedData.map((category) => (
                <CategoryItemRow
                  key={category.id}
                  category={category}
                  columns={categoriesColumns}
                  isSelected={selectedCategory?.id === category.id}
                  onSelect={onSelectCategory}
                  isCategoryTargetingVisible={isCategoryTargetingVisible}
                />
              ))}
        </div>

        {/* Loading indicator */}
        {loading && <div className="loading-img"></div>}

        {/* No data message */}
        {!loading && groupedData.length === 0 && (
          <div className="no-data">
            <span>{data?.length === 0 ? 'No categories found.' : 'No categories matching your filter.'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

CategoriesTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  hiddenColumnIds: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  title: PropTypes.string,
  selectedCategory: PropTypes.object,
  onSelectCategory: PropTypes.func,
  isCategoryTargetingVisible: PropTypes.bool,
};

CategoriesTable.defaultProps = {
  data: [],
  hiddenColumnIds: [],
  loading: false,
  selectedCategory: null,
  onSelectCategory: () => {},
};

export default CategoriesTable;
